/**
 * PhänotypIndicator — Zeigt den aktiven AYU-Phänotyp-Cluster im Chat an
 * Visueller Indikator welchem AYU-Cluster der Nutzer zugeordnet ist.
 * Verwendet ausschließlich funktionelle Cluster-Namen (keine Dosha-Begriffe).
 */
import { Leaf, Flame, Droplets, Wind, Shield } from "lucide-react";

interface PhänotypIndicatorProps {
  doshaType?: string | null;
  visible?: boolean;
  className?: string;
}

const CLUSTER_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string; bgColor: string }> = {
  // Neue funktionelle Cluster-Namen
  "haut & nervensystem": {
    label: "Haut & Nervensystem",
    icon: <Wind className="w-3 h-3" />,
    color: "text-blue-400",
    bgColor: "bg-blue-900/20 border-blue-800/30",
  },
  "entzündung & regulation": {
    label: "Entzündung & Regulation",
    icon: <Flame className="w-3 h-3" />,
    color: "text-orange-400",
    bgColor: "bg-orange-900/20 border-orange-800/30",
  },
  "metabolismus & struktur": {
    label: "Metabolismus & Struktur",
    icon: <Leaf className="w-3 h-3" />,
    color: "text-green-400",
    bgColor: "bg-green-900/20 border-green-800/30",
  },
  "stress": {
    label: "Stress-Cluster",
    icon: <Droplets className="w-3 h-3" />,
    color: "text-amber-400",
    bgColor: "bg-amber-900/20 border-amber-800/30",
  },
  "immunsystem & darm": {
    label: "Immunsystem & Darm",
    icon: <Shield className="w-3 h-3" />,
    color: "text-cyan-400",
    bgColor: "bg-cyan-900/20 border-cyan-800/30",
  },
  // Legacy-Werte für Rückwärtskompatibilität mit bestehenden Kunden
  "vata-trocken": {
    label: "Haut & Nervensystem",
    icon: <Wind className="w-3 h-3" />,
    color: "text-blue-400",
    bgColor: "bg-blue-900/20 border-blue-800/30",
  },
  "pitta-hitze": {
    label: "Entzündung & Regulation",
    icon: <Flame className="w-3 h-3" />,
    color: "text-orange-400",
    bgColor: "bg-orange-900/20 border-orange-800/30",
  },
  "kapha-stoffwechsel": {
    label: "Metabolismus & Struktur",
    icon: <Leaf className="w-3 h-3" />,
    color: "text-green-400",
    bgColor: "bg-green-900/20 border-green-800/30",
  },
  "allergie-immun-darm": {
    label: "Immunsystem & Darm",
    icon: <Shield className="w-3 h-3" />,
    color: "text-cyan-400",
    bgColor: "bg-cyan-900/20 border-cyan-800/30",
  },
  "vata": {
    label: "Haut & Nervensystem",
    icon: <Wind className="w-3 h-3" />,
    color: "text-blue-400",
    bgColor: "bg-blue-900/20 border-blue-800/30",
  },
  "pitta": {
    label: "Entzündung & Regulation",
    icon: <Flame className="w-3 h-3" />,
    color: "text-orange-400",
    bgColor: "bg-orange-900/20 border-orange-800/30",
  },
  "kapha": {
    label: "Metabolismus & Struktur",
    icon: <Leaf className="w-3 h-3" />,
    color: "text-green-400",
    bgColor: "bg-green-900/20 border-green-800/30",
  },
};

export default function DoshaModeIndicator({ doshaType, visible = true, className = "" }: PhänotypIndicatorProps) {
  if (!visible || !doshaType) return null;

  const config = CLUSTER_CONFIG[doshaType.toLowerCase()] || {
    label: "AYU-Phänotyp",
    icon: <Leaf className="w-3 h-3" />,
    color: "text-[#6b7f4e]",
    bgColor: "bg-[#6b7f4e]/10 border-[#d4dcc4]",
  };

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium border ${config.bgColor} ${config.color} ${className}`}
    >
      {config.icon}
      {config.label}
    </div>
  );
}
